
import { useState } from 'react';
import { mockPartners } from '../../../mocks/partners';

interface PartnerDetailProps {
  partner: typeof mockPartners[0];
  onClose: () => void;
}

export default function PartnerDetail({ partner, onClose }: PartnerDetailProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'credentials' | 'notes'>('overview');

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-10 pb-10 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl mx-4 animate-in">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 ${partner.logoBg} rounded-xl flex items-center justify-center flex-shrink-0`}>
              <i className={`${partner.logo} text-2xl ${partner.logoColor}`}></i>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{partner.name}</h2>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-xs text-gray-500">{partner.category}</span>
                <span className="text-gray-300">·</span>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                  partner.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                }`}>
                  {partner.status === 'active' ? 'Active' : 'Paused'}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100 px-6">
          {(['overview', 'credentials', 'notes'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-all cursor-pointer whitespace-nowrap capitalize ${
                activeTab === tab
                  ? 'border-[#0A0A0A] text-gray-900'
                  : 'border-transparent text-gray-400 hover:text-gray-600'
              }`}
            >
              {tab === 'credentials' ? 'Login & Access' : tab}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Financial Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-emerald-50 rounded-xl p-4">
                  <div className="text-xs text-emerald-600 font-medium mb-1">Total Earned</div>
                  <div className="text-2xl font-bold text-emerald-700">${partner.totalEarned.toLocaleString()}</div>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="text-xs text-gray-500 font-medium mb-1">Commission Rate</div>
                  <div className="text-2xl font-bold text-gray-900">{partner.commissionRate}</div>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="text-xs text-gray-500 font-medium mb-1">Cookie Duration</div>
                  <div className="text-2xl font-bold text-gray-900">{partner.cookieDuration}</div>
                </div>
              </div>

              {/* Mini Chart */}
              <div className="bg-gray-50 rounded-xl p-5">
                <div className="text-xs font-semibold text-gray-500 mb-3">Last 6 Months Earnings</div>
                <div className="h-24 flex items-end gap-2">
                  {partner.monthlyStats.map((val, i) => {
                    const max = Math.max(...partner.monthlyStats);
                    const pct = max > 0 ? (val / max) * 100 : 0;
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1">
                        <div className="text-[10px] text-gray-400 font-medium">${val}</div>
                        <div
                          className="w-full bg-[#0A0A0A] rounded-t-sm hover:bg-emerald-500 transition-colors cursor-pointer"
                          style={{ height: `${Math.max(pct, 4)}%` }}
                        ></div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Deal Details */}
              <div>
                <h4 className="text-sm font-bold text-gray-900 mb-3">Deal Details</h4>
                <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                  <div className="flex items-center justify-between py-2 border-b border-gray-50">
                    <span className="text-sm text-gray-500">Negotiated Price</span>
                    <span className="text-sm font-semibold text-gray-900">{partner.negotiatedPrice}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-gray-50">
                    <span className="text-sm text-gray-500">Commission Type</span>
                    <span className="text-sm font-semibold text-gray-900">{partner.commissionType}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-gray-50">
                    <span className="text-sm text-gray-500">Payment Schedule</span>
                    <span className="text-sm font-semibold text-gray-900">{partner.paymentSchedule}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-gray-50">
                    <span className="text-sm text-gray-500">Next Payout</span>
                    <span className="text-sm font-semibold text-gray-900">{partner.nextPayout}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-gray-50">
                    <span className="text-sm text-gray-500">Contract Start</span>
                    <span className="text-sm font-semibold text-gray-900">{partner.contractStart}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-gray-50">
                    <span className="text-sm text-gray-500">Contract End</span>
                    <span className="text-sm font-semibold text-gray-900">{partner.contractEnd}</span>
                  </div>
                </div>
              </div>

              {/* Contract File */}
              <div>
                <h4 className="text-sm font-bold text-gray-900 mb-3">Contract Document</h4>
                {partner.contractFile ? (
                  <div className="flex items-center gap-3 bg-gray-50 rounded-xl p-4">
                    <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-file-pdf-2-line text-xl text-red-500"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">{partner.contractFile}</div>
                      <div className="text-xs text-gray-400">Uploaded contract document</div>
                    </div>
                    <button className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-download-line mr-1"></i> Download
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 bg-gray-50 rounded-xl p-4 border-2 border-dashed border-gray-200">
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-upload-cloud-line text-xl text-gray-400"></i>
                    </div>
                    <div className="flex-1">
                      <div className="text-sm text-gray-500">No contract uploaded</div>
                      <div className="text-xs text-gray-400">Upload a PDF or link to the agreement</div>
                    </div>
                    <button className="px-3 py-1.5 text-xs font-medium text-white bg-[#0A0A0A] rounded-lg hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-upload-line mr-1"></i> Upload
                    </button>
                  </div>
                )}
              </div>

              {/* Contact */}
              <div>
                <h4 className="text-sm font-bold text-gray-900 mb-3">Partner Contact</h4>
                <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <i className="ri-user-line text-sm text-gray-400"></i>
                    <span className="text-sm text-gray-900">{partner.contactName}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="ri-mail-line text-sm text-gray-400"></i>
                    <a href={`mailto:${partner.contactEmail}`} className="text-sm text-teal-600 hover:text-teal-700 cursor-pointer" rel="nofollow">{partner.contactEmail}</a>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="ri-phone-line text-sm text-gray-400"></i>
                    <span className="text-sm text-gray-900">{partner.contactPhone}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'credentials' && (
            <div className="space-y-6">
              {/* Affiliate Portal */}
              <div>
                <h4 className="text-sm font-bold text-gray-900 mb-3">Affiliate Portal</h4>
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0 border border-gray-200">
                      <i className="ri-external-link-line text-lg text-gray-600"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900">Portal URL</div>
                      <a
                        href={partner.affiliatePortalUrl}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="text-xs text-teal-600 hover:text-teal-700 truncate block cursor-pointer"
                      >
                        {partner.affiliatePortalUrl}
                      </a>
                    </div>
                    <a
                      href={partner.affiliatePortalUrl}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="px-3 py-1.5 text-xs font-medium text-white bg-[#0A0A0A] rounded-lg hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Open Portal
                    </a>
                  </div>
                </div>
              </div>

              {/* Login Credentials */}
              <div>
                <h4 className="text-sm font-bold text-gray-900 mb-3">Login Credentials</h4>
                <div className="bg-gray-50 rounded-xl p-4 space-y-4">
                  <div>
                    <label className="text-xs text-gray-500 font-medium mb-1 block">Email / Username</label>
                    <div className="flex items-center gap-2 bg-white rounded-lg border border-gray-200 px-3 py-2.5">
                      <i className="ri-mail-line text-sm text-gray-400"></i>
                      <span className="text-sm text-gray-900 flex-1">{partner.loginEmail}</span>
                      <button
                        onClick={() => navigator.clipboard.writeText(partner.loginEmail)}
                        className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors cursor-pointer"
                        title="Copy"
                      >
                        <i className="ri-file-copy-line text-sm"></i>
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 font-medium mb-1 block">Password</label>
                    <div className="flex items-center gap-2 bg-white rounded-lg border border-gray-200 px-3 py-2.5">
                      <i className="ri-lock-line text-sm text-gray-400"></i>
                      <span className="text-sm text-gray-900 flex-1 font-mono">
                        {showPassword ? 'Str0ng!Pass#2024' : partner.loginPassword}
                      </span>
                      <button
                        onClick={() => setShowPassword(!showPassword)}
                        className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors cursor-pointer"
                        title={showPassword ? 'Hide' : 'Show'}
                      >
                        <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-sm`}></i>
                      </button>
                      <button
                        onClick={() => navigator.clipboard.writeText('Str0ng!Pass#2024')}
                        className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors cursor-pointer"
                        title="Copy"
                      >
                        <i className="ri-file-copy-line text-sm"></i>
                      </button>
                    </div>
                  </div>
                </div>
                <p className="text-[11px] text-gray-400 mt-2 flex items-center gap-1">
                  <i className="ri-shield-check-line"></i>
                  Credentials are stored locally and never shared
                </p>
              </div>

              {/* Quick Actions */}
              <div>
                <h4 className="text-sm font-bold text-gray-900 mb-3">Quick Actions</h4>
                <div className="grid grid-cols-2 gap-3">
                  <a
                    href={partner.affiliatePortalUrl}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="flex items-center gap-3 bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-colors cursor-pointer"
                  >
                    <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center flex-shrink-0 border border-gray-200">
                      <i className="ri-dashboard-line text-gray-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">Open Dashboard</div>
                      <div className="text-xs text-gray-400">View affiliate stats</div>
                    </div>
                  </a>
                  <a
                    href={`mailto:${partner.contactEmail}`}
                    className="flex items-center gap-3 bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-colors cursor-pointer"
                    rel="nofollow"
                  >
                    <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center flex-shrink-0 border border-gray-200">
                      <i className="ri-mail-send-line text-gray-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">Email Contact</div>
                      <div className="text-xs text-gray-400">{partner.contactName}</div>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'notes' && (
            <div className="space-y-6">
              <div>
                <h4 className="text-sm font-bold text-gray-900 mb-3">Notes</h4>
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-sm text-gray-700 leading-relaxed">{partner.notes}</p>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-bold text-gray-900 mb-3">Tags</h4>
                <div className="flex flex-wrap gap-2">
                  {partner.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-sm font-bold text-gray-900 mb-3">Add a Note</h4>
                <textarea
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0A0A0A]/10 focus:border-gray-300 resize-none"
                  rows={4}
                  maxLength={500}
                  placeholder="Write a note about this partnership..."
                ></textarea>
                <div className="flex justify-end mt-2">
                  <button className="px-4 py-2 bg-[#0A0A0A] text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap">
                    Save Note
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
